import React, { useState, useEffect, useMemo } from "react";
import { useTheme } from "@material-ui/core/styles";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Popover,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  InputAdornment,
  Chip,
  Divider,
  Box,
} from "@mui/material";
import { i18n } from "../../translate/i18n";
import {
  Close as CloseIcon,
  Help as HelpIcon,
  CompareArrows as CompareArrowsIcon,
  Code as CodeIcon,
  Search as SearchIcon,
  Tag as TagIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  DragIndicator as DragHandleIcon,
} from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

const generateId = () => Math.random().toString(36).substr(2, 9);

const FlowBuilderConditionCompareModal = ({ open, onSave, data, onUpdate, close, nodes }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.type === 'dark';

  // Main state: list of conditions
  const [conditions, setConditions] = useState([]);

  // UI State
  const [view, setView] = useState("list"); // 'list', 'form'
  const [editingId, setEditingId] = useState(null);

  // Form State
  const [leftValue, setLeftValue] = useState("");
  const [rightValue, setRightValue] = useState("");
  const [operator, setOperator] = useState("equals");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [name, setName] = useState("");

  // Variable Selector State
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeField, setActiveField] = useState(null);

  const availableVariables = useMemo(() => {
    const baseVariables = [
      { name: "nome", description: i18n.t("flowBuilder.conditionCompare.variables.contactName") || "Nome do contato", category: "contact" },
      { name: "email", description: i18n.t("flowBuilder.conditionCompare.variables.contactEmail") || "Email do contato", category: "contact" },
      { name: "telefone", description: i18n.t("flowBuilder.conditionCompare.variables.contactPhone") || "Telefone do contato", category: "contact" },
      { name: "ultimaMensagem", description: i18n.t("flowBuilder.conditionCompare.variables.lastMessage") || "Última mensagem recebida", category: "message" },
      { name: "dataContato", description: i18n.t("flowBuilder.conditionCompare.variables.contactDate") || "Data do último contato", category: "contact" },
      { name: "apiResponse", description: i18n.t("flowBuilder.conditionCompare.variables.apiResponse") || "Resposta da última requisição HTTP", category: "api" },
    ];

    const flowVariables = [];
    if (Array.isArray(nodes)) {
      nodes.forEach(node => {
        if (node.type === "input") {
          // Variable principal
          if (node.data?.variableName) {
            flowVariables.push({
              name: node.data.variableName,
              description: i18n.t("flowBuilder.conditionCompare.variables.localVariable") || "Variável local do fluxo",
              category: "userFlow"
            });
          }
          // [ANTIGRAVITY] Variables de Remarketing
          if (Array.isArray(node.data?.remarketingTasks)) {
            node.data.remarketingTasks.forEach(task => {
              if (task.variable) {
                flowVariables.push({
                  name: task.variable,
                  description: i18n.t("flowBuilder.conditionCompare.variables.remarketingVariable") || "Variable de Remarketing",
                  category: "userFlow"
                });
              }
            });
          }
        }
        if (node.type === "variable" && node.data?.variableName) {
          flowVariables.push({
            name: node.data.variableName,
            description: i18n.t("flowBuilder.conditionCompare.variables.globalVariable") || "Variável global do fluxo",
            category: "userFlow"
          });
        }
        if (node.type === "httpRequest" && Array.isArray(node.data?.saveVariables)) {
          node.data.saveVariables.forEach(v => {
            if (v.variable) {
              flowVariables.push({
                name: v.variable,
                description: i18n.t("flowBuilder.conditionCompare.variables.apiVariable") || "Variável de resposta API",
                category: "userFlow"
              });
            }
          });
        }
        // [ANTIGRAVITY] Add OpenAI Output Variable
        if (node.type === "openai" && node.data?.typebotIntegration?.outputVariableName) {
          flowVariables.push({
            name: node.data.typebotIntegration.outputVariableName,
            description: i18n.t("flowBuilder.conditionCompare.variables.ai_action") || "Acción IA",
            category: "userFlow"
          });
        }
        // [ANTIGRAVITY] Add Gemini Output Variable
        if (node.type === "gemini" && node.data?.typebotIntegration?.outputVariableName) {
          flowVariables.push({
            name: node.data.typebotIntegration.outputVariableName,
            description: i18n.t("flowBuilder.conditionCompare.variables.gemini_action") || "Acción Gemini",
            category: "userFlow"
          });
        }
      });
    }

    const uniqueFlowVariables = flowVariables.filter((v, index, self) =>
      index === self.findIndex((t) => t.name === v.name)
    );

    return [...baseVariables, ...uniqueFlowVariables];
  }, [nodes]);

  const comparisonOperators = [
    { value: "equals", label: i18n.t("flowBuilder.conditionCompare.operators.equals") || "Igual a", icon: "=" },
    { value: "notEquals", label: i18n.t("flowBuilder.conditionCompare.operators.notEquals") || "Diferente de", icon: "≠" },
    { value: "contains", label: i18n.t("flowBuilder.conditionCompare.operators.contains") || "Contém", icon: "∈" },
    { value: "startsWith", label: i18n.t("flowBuilder.conditionCompare.operators.startsWith") || "Começa com", icon: "⊰" },
    { value: "endsWith", label: i18n.t("flowBuilder.conditionCompare.operators.endsWith") || "Termina com", icon: "⊱" },
    { value: "greaterThan", label: i18n.t("flowBuilder.conditionCompare.operators.greaterThan") || "Maior que", icon: ">" },
    { value: "lessThan", label: i18n.t("flowBuilder.conditionCompare.operators.lessThan") || "Menor que", icon: "<" },
    { value: "greaterOrEqual", label: i18n.t("flowBuilder.conditionCompare.operators.greaterOrEqual") || "Maior ou igual a", icon: "≥" },
    { value: "lessOrEqual", label: i18n.t("flowBuilder.conditionCompare.operators.lessOrEqual") || "Menor ou igual a", icon: "≤" },
    { value: "isEmpty", label: i18n.t("flowBuilder.conditionCompare.operators.isEmpty") || "Está vazio", icon: "∅" },
    { value: "isNotEmpty", label: i18n.t("flowBuilder.conditionCompare.operators.isNotEmpty") || "Não está vazio", icon: "¬∅" },
    { value: "regex", label: i18n.t("flowBuilder.conditionCompare.operators.regex") || "Regex (Expressão Regular)", icon: ".*" },
    { value: "regex_i", label: i18n.t("flowBuilder.conditionCompare.operators.regex_i") || "Regex (Ignorar Mayúsculas)", icon: ".*i" },
    { value: "containsAny", label: i18n.t("flowBuilder.conditionCompare.operators.containsAny") || "Contiene alguna de (separar con coma)", icon: "∈+" },
    { value: "notContainsAny", label: i18n.t("flowBuilder.conditionCompare.operators.notContainsAny") || "No contiene ninguna de (separar con coma)", icon: "∉+" },
  ];

  useEffect(() => {
    if (open === "edit" && data) {
      const loadedConditions = data.data.conditions || [];
      // Backward compatibility
      if (loadedConditions.length === 0 && data.data.leftValue) {
        loadedConditions.push({
          id: 'true', // Legacy ID
          leftValue: data.data.leftValue,
          operator: data.data.operator,
          rightValue: data.data.rightValue
        });
      }
      setConditions(loadedConditions);
      setName(data.data.name || "");
    } else {
      setConditions([]);
      setName("");
    }
    setView("list");
    setEditingId(null);
    resetForm();
  }, [open, data]);

  const resetForm = () => {
    setLeftValue("");
    setRightValue("");
    setOperator("equals");
    setError("");
  };

  const isUnaryOperator = operator === "isEmpty" || operator === "isNotEmpty";

  // --- Handlers ---

  const handleAddNew = () => {
    resetForm();
    setEditingId(null);
    setView("form");
  };

  const handleEdit = (cond) => {
    setLeftValue(cond.leftValue || "");
    setRightValue(cond.rightValue || "");
    setOperator(cond.operator || "equals");
    setEditingId(cond.id);
    setView("form");
  };

  const handleDelete = (id) => {
    setConditions(conditions.filter(c => c.id !== id));
  };

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const items = Array.from(conditions);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setConditions(items);
  };

  const handleSaveCondition = () => {
    if (!leftValue) {
      setError(i18n.t("flowBuilder.conditionCompare.errorValue1") || "O primeiro valor é obrigatório");
      return;
    }
    if (!isUnaryOperator && !rightValue) {
      setError(i18n.t("flowBuilder.conditionCompare.errorValue2") || "O segundo valor é obrigatório");
      return;
    }

    const newCondition = {
      id: editingId || generateId(),
      leftValue,
      operator,
      rightValue: isUnaryOperator ? "" : rightValue
    };

    if (editingId) {
      setConditions(conditions.map(c => c.id === editingId ? newCondition : c));
    } else {
      setConditions([...conditions, newCondition]);
    }

    setView("list");
    resetForm();
  };

  const handleFinalSave = () => {
    setIsSubmitting(true);
    try {
      const currentNodeData = {
        conditions: conditions,
        name: name
      };

      if (open === "edit" && data) {
        onUpdate({
          ...data,
          data: currentNodeData,
        });
      } else {
        onSave(currentNodeData);
      }
      setIsSubmitting(false);
      close(false);
    } catch (err) {
      console.error(err);
      setIsSubmitting(false);
    }
  };

  // --- Variable Helper ---
  const handleVariableClick = (event, field) => {
    setActiveField(field);
    setAnchorEl(event.currentTarget);
    setSearchTerm("");
  };

  const insertVariable = (variableName) => {
    const value = "\${" + variableName + "}";
    if (activeField === "left") setLeftValue(leftValue + value);
    else setRightValue(rightValue + value);
    setAnchorEl(null);
  };

  const filteredVariables = availableVariables.filter(
    (variable) =>
      variable.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      variable.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const categoryOrder = ["contact", "message", "api", "userFlow"];
  const groupedVariables = groupedVariablesMemo(availableVariables);

  return (
    <Dialog
      open={open !== false}
      onClose={() => close(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 2,
          bgcolor: isDarkMode ? "#333333" : "#ffffff",
          color: isDarkMode ? "#ffffff" : "inherit"
        }
      }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", bgcolor: isDarkMode ? "#222222" : "#f8f9fa", borderBottom: 1, borderColor: "divider" }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {view === "form" && (
            <IconButton size="small" onClick={() => setView("list")}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <CompareArrowsIcon sx={{ color: "#9c27b0" }} />
          <Typography variant="h6">
            {view === "form"
              ? (editingId ? i18n.t("flowBuilder.conditionCompare.editTitle") || "Editar Condição" : i18n.t("flowBuilder.conditionCompare.addTitle") || "Nova Condição")
              : i18n.t("flowBuilder.conditionCompare.manageConditions") || "Gerenciar Condições"}
          </Typography>
        </Stack>
        <IconButton size="small" onClick={() => close(false)} sx={{ color: isDarkMode ? "#ffffff" : "inherit" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, minHeight: 400 }}>
        {view === "list" ? (
          <Stack spacing={2}>
            {conditions.length === 0 && (
              <Typography variant="body2" align="center" sx={{ py: 4, color: isDarkMode ? "#b3b3b3" : "text.secondary" }}>
                {i18n.t("flowBuilder.conditionCompare.noConditionsAdded") || "Nenhuma condição adicionada. Adicione condições para criar ramificações no fluxo."}
              </Typography>
            )}

            <Stack spacing={1} sx={{ mb: 2 }}>
              <Typography variant="subtitle2">{i18n.t("flowBuilder.form.name") || "Nome"}</Typography>
              <TextField
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                size="small"
                variant="outlined"
                InputProps={{
                  style: {
                    color: isDarkMode ? "#ffffff" : "inherit"
                  }
                }}
                InputLabelProps={{
                  style: {
                    color: isDarkMode ? "#b3b3b3" : "inherit"
                  }
                }}
              />
            </Stack>

            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="conditions">
                {(provided) => (
                  <List
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {conditions.map((item, index) => (
                      <Draggable key={item.id} draggableId={item.id} index={index}>
                        {(provided) => (
                          <ListItem
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            sx={{
                              bgcolor: isDarkMode ? "#2a2a2a" : "#ffffff",
                              border: 1,
                              borderColor: isDarkMode ? "#444" : "divider",
                              borderRadius: 1,
                              mb: 1
                            }}
                            secondaryAction={
                              <Stack direction="row">
                                <IconButton size="small" onClick={() => handleEdit(item)}>
                                  <EditIcon fontSize="small" color="primary" />
                                </IconButton>
                                <IconButton size="small" onClick={() => handleDelete(item.id)}>
                                  <DeleteIcon fontSize="small" color="error" />
                                </IconButton>
                              </Stack>
                            }
                          >
                            <ListItemIcon {...provided.dragHandleProps} sx={{ minWidth: 36, cursor: 'grab' }}>
                              <DragHandleIcon color="action" />
                            </ListItemIcon>
                            <Box sx={{ width: '100%', pr: 7 }}>
                              <Typography variant="caption" fontWeight="bold" sx={{ color: isDarkMode ? "#a78bfa" : "primary.main" }}>
                                {i18n.t("flowBuilder.conditionCompare.output") || "SAÍDA"} {index + 1}
                              </Typography>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap" }}>
                                <Typography variant="body2" sx={{ wordBreak: 'break-all', color: isDarkMode ? "#ffffff" : "text.primary" }}>{item.leftValue}</Typography>
                                <Chip
                                  label={comparisonOperators.find(op => op.value === item.operator)?.label || item.operator}
                                  size="small"
                                  sx={{ height: 20, fontSize: '0.65rem', bgcolor: isDarkMode ? "#444" : "default", color: isDarkMode ? "#fff" : "default" }}
                                />
                                <Typography variant="body2" sx={{ wordBreak: 'break-all', color: isDarkMode ? "#ffffff" : "text.primary" }}>
                                  {item.operator !== 'isEmpty' && item.operator !== 'isNotEmpty' ? item.rightValue : ""}
                                </Typography>
                              </Stack>
                            </Box>
                          </ListItem>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </List>
                )}
              </Droppable>
            </DragDropContext>

            <Button
              variant="dashed"
              startIcon={<AddIcon />}
              onClick={handleAddNew}
              fullWidth
              sx={{ border: '1px dashed #ccc', color: isDarkMode ? "#ffffff" : "text.secondary", py: 1.5 }}
            >
              {i18n.t("flowBuilder.conditionCompare.addCondition") || "Adicionar Condição"}
            </Button>
          </Stack>
        ) : (
          /* FORM VIEW */
          <Stack spacing={3}>
            <Stack spacing={1}>
              <Typography variant="subtitle2" sx={{ color: isDarkMode ? "#ffffff" : "inherit" }}>{i18n.t("flowBuilder.form.name") || "Nome"}</Typography>
              <TextField
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                size="small"
                variant="outlined"
                sx={{
                  input: { color: isDarkMode ? "#ffffff" : "inherit" },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: isDarkMode ? '#555' : 'rgba(0, 0, 0, 0.23)' },
                    '&:hover fieldset': { borderColor: isDarkMode ? '#888' : 'rgba(0, 0, 0, 0.87)' },
                  }
                }}
              />
            </Stack>
            <Stack spacing={1}>
              <Typography variant="subtitle2" sx={{ color: isDarkMode ? "#ffffff" : "inherit" }}>{i18n.t("flowBuilder.conditionCompare.value1") || "Valor 1"}</Typography>
              <TextField
                fullWidth
                value={leftValue}
                onChange={(e) => setLeftValue(e.target.value)}
                size="small"
                sx={{
                  input: { color: isDarkMode ? "#ffffff" : "inherit" },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: isDarkMode ? '#555' : 'rgba(0, 0, 0, 0.23)' },
                    '&:hover fieldset': { borderColor: isDarkMode ? '#888' : 'rgba(0, 0, 0, 0.87)' },
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={(e) => handleVariableClick(e, "left")}>
                        <CodeIcon color="primary" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

            <FormControl fullWidth size="small">
              <InputLabel style={{ color: isDarkMode ? "#b3b3b3" : "inherit" }}>{i18n.t("flowBuilder.conditionCompare.operatorLabel") || "Operador"}</InputLabel>
              <Select
                value={operator}
                label={i18n.t("flowBuilder.conditionCompare.operatorLabel") || "Operador"}
                onChange={(e) => setOperator(e.target.value)}
                style={{ color: isDarkMode ? "#ffffff" : "inherit" }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      minWidth: '450px',
                      maxWidth: '90vw'
                    },
                  },
                }}
              >
                {comparisonOperators.map(op => (
                  <MenuItem key={op.value} value={op.value} style={{ whiteSpace: 'normal', paddingRight: 24 }}>
                    {op.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {!isUnaryOperator && (
              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ color: isDarkMode ? "#ffffff" : "inherit" }}>{i18n.t("flowBuilder.conditionCompare.value2") || "Valor 2"}</Typography>
                <TextField
                  fullWidth
                  value={rightValue}
                  onChange={(e) => setRightValue(e.target.value)}
                  size="small"
                  sx={{
                    input: { color: isDarkMode ? "#ffffff" : "inherit" },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: isDarkMode ? '#555' : 'rgba(0, 0, 0, 0.23)' },
                      '&:hover fieldset': { borderColor: isDarkMode ? '#888' : 'rgba(0, 0, 0, 0.87)' },
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={(e) => handleVariableClick(e, "right")}>
                          <CodeIcon color="primary" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
            )}

            {error && <Typography color="error" variant="caption">{error}</Typography>}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: isDarkMode ? "#222222" : "background.paper", borderTop: 1, borderColor: "divider" }}>
        {view === "list" ? (
          <>
            <Button 
              onClick={() => close(false)}
              variant="outlined"
              sx={{ 
                textTransform: "none",
                color: "#ff4d4f",
                borderColor: "#ff4d4f",
                fontWeight: "bold", 
                borderRadius: "8px"
              }}
            >
              {i18n.t("flowBuilder.conditionCompare.cancel") || "Cancelar"}
            </Button>
            <Button 
              variant="contained" 
              onClick={handleFinalSave} 
              disabled={isSubmitting}
              sx={{ 
                textTransform: "none", 
                color: "white",
                backgroundColor: "#7367F0",
                fontWeight: "bold", 
                borderRadius: "8px", 
                boxShadow: "0 4px 14px 0 rgba(115, 103, 240, 0.39)",
                "&:hover": {
                  backgroundColor: "#5e50ee",
                },
                "&.Mui-disabled": {
                  backgroundColor: isDarkMode ? "#555" : "#ccc",
                  color: isDarkMode ? "#aaa" : "#888"
                }
              }}
            >
              {isSubmitting ? (i18n.t("flowBuilder.conditionCompare.saving") || "Salvando...") : i18n.t("common.save")}
            </Button>
          </>
        ) : (
          <>
            <Button 
              onClick={() => {
                setView("list");
                setError("");
              }}
              variant="outlined"
              sx={{ 
                textTransform: "none",
                color: "#ff4d4f",
                borderColor: "#ff4d4f",
                fontWeight: "bold", 
                borderRadius: "8px"
              }}
            >
              {i18n.t("flowBuilder.conditionCompare.cancel") || "Cancelar"}
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSaveCondition}
              sx={{ 
                textTransform: "none", 
                color: "white",
                backgroundColor: "#7367F0",
                fontWeight: "bold", 
                borderRadius: "8px", 
                boxShadow: "0 4px 14px 0 rgba(115, 103, 240, 0.39)",
                "&:hover": {
                  backgroundColor: "#5e50ee",
                }
              }}
            >
              {editingId ? (i18n.t("flowBuilder.conditionCompare.updateCondition") || "Actualizar") : (i18n.t("flowBuilder.conditionCompare.addCondition") || "Agregar")}
            </Button>
          </>
        )}
      </DialogActions>

      {/* Popover for Variables */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Paper sx={{ p: 2, width: 300 }}>
          <TextField
            fullWidth size="small"
            placeholder={i18n.t("flowBuilder.conditionCompare.placeholderSearch") || "Buscar variável..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 1 }}
          />
          <List sx={{ maxHeight: 200, overflow: 'auto' }}>
            {categoryOrder.map(cat => {
              const vars = groupedVariables[cat];
              if (!vars || vars.length === 0) return null;
              return (
                <React.Fragment key={cat}>
                  <ListItem sx={{ py: 0, bgcolor: '#eee' }}>
                    <Typography variant="caption" fontWeight="bold">{i18n.t(`flowBuilder.conditionCompare.categories.${cat}`) || cat.toUpperCase()}</Typography>
                  </ListItem>
                  {vars.filter(v =>
                    v.name.toLowerCase().includes(searchTerm.toLowerCase())
                  ).map(v => (
                    <ListItem key={v.name} button onClick={() => insertVariable(v.name)}>
                      <ListItemText primary={v.name} secondary={v.description} />
                    </ListItem>
                  ))}
                </React.Fragment>
              )
            })}
          </List>
        </Paper>
      </Popover>
    </Dialog>
  );
};

function groupedVariablesMemo(vars) {
  return vars.reduce((acc, v) => {
    if (!acc[v.category]) acc[v.category] = [];
    acc[v.category].push(v);
    return acc;
  }, {});
}

export default FlowBuilderConditionCompareModal;
